/**
 * Getting the bytes of one file to where the studio reserved a place for them
 * (D-909).
 *
 * **Both doors do the same three things** — reserve a destination, write the
 * bytes, then finish (a claim for a practice video, the message send for an
 * attachment). The middle step was written twice, and the two copies had
 * already drifted: one reported progress and took an abort signal, the other
 * did neither; one read a refusal three ways, the other two; and they gave
 * *opposite* readings of the same 403. This is that middle step, once.
 *
 * **What stays with the caller, deliberately**: reserving, finishing, and every
 * sentence a person reads. A core that knows which door it is serving has
 * stopped being a core — so the wording is passed in, and what a status *means*
 * is the caller's to decide. This returns what happened; it does not judge it.
 */

import { send } from './upload'

/** Where the server said to put this file. */
export type Destination = {
  /**
   * Straight to storage, skipping the app entirely — which is what keeps a
   * video off the worker that is also serving the register at 4:30.
   *
   * **Null means this host issues no signed URLs** — local disk, so a
   * developer's machine and the whole test suite (D-669). Then the bytes go
   * through the app instead, in one request.
   */
  uploadUrl: string | null
  /** Where the bytes go when there is no `uploadUrl`. */
  throughApp: string
  /**
   * What to call the bytes, when the file itself will not say.
   *
   * ⚠ **A phone often reports no type at all for a camera-roll file**, which
   * `whyNotAVideo` deliberately lets through — so the practice door has always
   * sent `video/mp4` in that case, at both the reserve and the write. The first
   * version of this core dropped that fallback and sent `Content-Type: ""` on
   * the one input that file is written around. Defaults to the file's own type.
   */
  contentType?: string
}

/** How this door words the one thing the core is allowed to say. */
export type TransferWording = {
  /**
   * Names the step when the transfer dies (D-899), or **undefined to leave the
   * transport's own sentence exactly as it is**.
   *
   * A rejected request is a bare `TypeError: Failed to fetch`, which names no
   * step — and a refused reservation and a dead transfer have opposite
   * remedies. The size goes in the sentence because a transfer that dies on a
   * phone and one a limit refuses look identical without it.
   *
   * ⚠ **Optional, and that is the whole point.** The first version of this
   * field was required, so the practice-video door passed its existing
   * *sentence* where a *step name* was wanted and a teacher on cellular read
   * `"The upload stopped before it finished.: The upload stopped before it
   * finished."` — two contradictory halves with a `.:` between them, on the
   * live path. A door that has no step to name says nothing rather than being
   * made to invent one.
   */
  sending?: (megabytes: number) => string
}

/** What happened. What it *means* is the caller's business. */
export type Transferred = {
  status: number
  /** Which door the bytes actually went through, since the caller may care. */
  via: 'storage' | 'app'
  /** The body, where there was one — the app's refusals carry a sentence. */
  text: string
  /**
   * **The bytes may already be there, and only the finishing step can tell.**
   *
   * A create-only destination refuses a second commit *exactly* as it refuses
   * an expired one — so a refused commit is as likely to mean "this already
   * landed while the phone changed network and lost the answer" as it is to
   * mean a real failure. Both doors have a step after this one that asks
   * storage what is actually there (the claim, or the message send), so the
   * honest thing is to let that step settle it rather than tell her it failed.
   *
   * ⚠ **This is only ever true of the commit**, never of a block. `observed`
   * against the real account: a `Put Block` *after* the blob is committed
   * answers **201**, not 403 — so blocks are not protected at all, and the
   * whole one-write guarantee (D-751) rests on this one refusal.
   */
  settleWithTheFinishingStep: boolean
}

/**
 * How big a block is, and how many go at once.
 *
 * **Both measured, not chosen** (F23, D-911, 2026-10-19), driving real Edge against
 * the real storage account with a 51.8 MB video:
 *
 * | | |
 * |---|---|
 * | one request, as before | 1.3 s |
 * | blocks, one at a time | 2.9 s — **2.2x slower than not chunking** |
 * | blocks, **four** at a time | 1.3 s — parity |
 * | blocks, eight at a time | 3.0 s — worse again |
 *
 * And on a throttled 4 Mbps link, which is the connection this exists for,
 * four-at-a-time came in at **109.8 s against 109.7 s** for a single request:
 * free, to within a tenth of a second.
 *
 * ⚠ **Sequential blocks are slower than not chunking at all**, because N
 * requests each paying a round trip cannot pipeline the way one streamed body
 * does. So the concurrency is not a tuning knob to be left at one and revisited
 * — it is what makes chunking cost nothing. **Do not raise it above four**:
 * eight measured worse than one.
 */
const BLOCK_BYTES = 4 * 1024 * 1024
const BLOCKS_IN_FLIGHT = 4

/**
 * How long to wait before trying a block again, and how many times.
 *
 * **The same shape as `live.ts`'s `RETRY_LADDER`** — bounded, widening, and it
 * **stops** rather than retrying for as long as the tab is open. Five rungs
 * summing to under two minutes.
 *
 * ⚠ **Copy that ladder's shape and its tests; never its visibility handler.**
 * `live.ts` *closes* on `hidden` and treats reappearing as a fresh start, which
 * is right for a stream and the exact opposite of right for an upload — doing
 * it here would guarantee the failure this feature exists to fix.
 */
const LADDER = [2_000, 5_000, 15_000, 30_000, 60_000] as const

/**
 * Block ids must be the same length as each other once decoded, and unique.
 * Padding to twelve digits holds past Azure's 50,000-block ceiling, and a
 * base64 of digits alone can never contain `+`, `/` or `=` — so nothing here
 * needs escaping in a URL or in the commit's XML.
 */
function blockId(index: number): string {
  return btoa(String(index).padStart(12, '0'))
}

/**
 * Resolves when the tab is in front of somebody — at once, if it already is.
 *
 * **A ladder that keeps counting while the screen is off burns itself out on
 * the sleep** and presents *"it gave up"* the moment she comes back, which is
 * the reported fault wearing a different sentence. Waiting here means the
 * rungs are spent on a connection that could actually answer.
 */
function whenInFront(stopped: Promise<void>): Promise<void> {
  if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    const finish = () => {
      document.removeEventListener('visibilitychange', onShown)
      resolve()
    }

    const onShown = () => {
      if (document.visibilityState !== 'hidden') finish()
    }

    document.addEventListener('visibilitychange', onShown)

    /*
     * ⚠ **There has to be a way out other than coming back.** Waiting only on
     * visibility means a transfer whose failure is *already known* parks here
     * and never settles — `Promise.all` is held by the parked worker, so she
     * backgrounds the app, the reservation is refused, and the tile sits on a
     * frozen bar with no error for as long as that tab stays behind. The
     * mutation never fires, `onError` never runs, and the listener is held for
     * ever alongside a promise nobody can resolve.
     */
    void stopped.then(finish)
  })
}

function pause(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer)
      // `{ once: true }` only removes it when it fires; a rung that simply
      // expires would otherwise leave a listener on the signal per retry.
      signal?.removeEventListener('abort', done)
      resolve()
    }

    const timer = setTimeout(done, milliseconds)
    signal?.addEventListener('abort', done)
  })
}

/**
 * Whether sending this block again could plausibly work.
 *
 * **A permission does not heal.** The destination is create-only and
 * short-lived, so a 4xx means it is spent or expired and every retry is a
 * request that cannot succeed — which on a weak connection is the worst place
 * to spend two minutes. A 5xx, a 408 or a 429 is storage having a moment, and
 * those are exactly what a ladder is for.
 */
function worthAnotherGo(status: number): boolean {
  return status >= 500 || status === 408 || status === 429
}

/**
 * Whether the file itself has gone out from under us (O9).
 *
 * ⚠ **A `Blob` slice is a lazy reference to bytes on disk**, not a copy. One
 * streamed request read the whole file once, up front; blocks re-read it over
 * minutes, and *resume* re-reads it at the moment the handle is least likely
 * to still be good — after the OS has had the app suspended. The transport
 * reports that as a dead connection, so the ladder would spend five rungs on a
 * fault no retry can fix and then show a sentence naming no remedy.
 *
 * **One byte is enough to ask**, and it costs nothing on the happy path
 * because this only runs after a failure.
 */
async function stillReadable(slice: Blob): Promise<boolean> {
  try {
    // **The byte has to arrive, not merely not throw.** A file truncated to
    // nothing hands back an empty buffer rather than failing, so a bare
    // `await` would call it readable and spend the whole ladder anyway.
    return (await slice.slice(0, 1).arrayBuffer()).byteLength === 1
  } catch {
    return false
  }
}

/** The signed URL always carries a query, so these are always `&`. */
function withQuery(url: string, extra: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}${extra}`
}

/**
 * ⚠ **Exactly these two headers, and never any others.**
 *
 * `observed` against the real storage account: the CORS rule admits
 * `x-ms-blob-type`, `content-type` and `content-length` only, and a preflight
 * carrying `x-ms-blob-content-type` is refused **403**. It is safe to omit
 * because nothing ever reads the blob's own content type — both serve paths
 * pass the app's stored `ContentType` instead.
 *
 * **The obvious implementation gets this wrong**: Azure's own SDK sets the
 * blob's content type when it commits, so anybody reaching for a helper will
 * fail the preflight — and what that surfaces as is *"the upload stopped
 * before it finished"*, which is the sentence this whole area exists to remove
 * (D-672).
 */
function storageHeaders(contentType: string): Record<string, string> {
  return { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': contentType }
}

/**
 * The whole file in blocks, then one commit (D-904).
 *
 * **The fraction counts confirmed bytes and nothing else.** `send` reports
 * `onProgress(1)` on *any* answer, a refusal included, so forwarding its
 * progress per block would credit a refused block as finished and march the
 * bar to 100% on a transfer that failed — destroying the one instrument D-901
 * exists to be. A block counts when storage has accepted it, which is also why
 * the fraction can only ever go up.
 */
async function putInBlocks(
  url: string,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
  ladder: readonly number[] = LADDER,
  onRate?: (measured: Measured) => void,
): Promise<Transferred> {
  const jobs: { id: string; from: number }[] = []

  for (let from = 0, i = 0; from < file.size; from += BLOCK_BYTES, i++) {
    jobs.push({ id: blockId(i), from })
  }

  let confirmed = 0
  let next = 0
  let refused: Transferred | null = null

  /**
   * Measured once, on the first **full round** of blocks storage confirms
   * (D-912).
   *
   * ⚠ **Not on the first block, which is what this was and which was wrong by
   * exactly `BLOCKS_IN_FLIGHT`.** The clock starts before any worker does, and
   * four blocks then cross one shared link together — so when the first of them
   * lands, the elapsed window paid for **four** blocks and `confirmed` names
   * only one. `observed` against a fake link of fixed bandwidth: the reported
   * rate came out at a ratio of `min(blocks, 4)` to the truth, 1.95 / 2.95 /
   * 4.01 for two, three and ten blocks.
   *
   * Everything downstream read that quarter-rate as gospel: the wait she was
   * quoted was four times too long, the offer fired on the studio's wifi where
   * it is supposed to decline, and D-903's encode deadline — which exists for a
   * phone with no hardware encoder — was four times too generous. Waiting for
   * the round makes the bytes and the clock describe the same window.
   */
  const measureAfter = Math.min(file.size, BLOCKS_IN_FLIGHT * BLOCK_BYTES)
  const begun = performance.now()
  let rateReported = false

  /**
   * ⚠ **A window that contains a wait describes the wait, not her connection.**
   *
   * `begun` is taken once, and every ladder rung and every park for a hidden
   * tab happens inside it while `confirmed` does not move — so the same ratio
   * defect, reached from the clock instead of the bytes. `observed` on a 40 MB
   * file where every block failed once behind a 400 ms rung: **37 MB/s against
   * 3484 MB/s** clean, a 93x understatement. **And its trigger is the fault
   * this whole feature was built for**: the phone sleeps, all four blocks fail,
   * all four workers park in `whenInFront` until she comes back, and five
   * minutes later a 500 KB/s link measures 55 KB/s.
   *
   * **Suppressed rather than corrected, and that is the deliberate half.**
   * Subtracting the idle time means the *union* of four workers' park
   * intervals, since three transferring while one waits is a link still fully
   * used — new arithmetic in the most delicate function here. Against that, the
   * cost of being wrong is not a missing banner but an actively harmful one: a
   * quoted wait of 26 minutes on an upload with two left, an offer she accepts,
   * a nearly-finished transfer thrown away, and `worthSeconds` so large that
   * D-903's encode deadline stops existing. **No offer beats a wrong offer**,
   * and the chunking half still carries the upload either way.
   *
   * The cost is real and is written down: on a link flaky enough to blip inside
   * the first 16 MB, the offer never appears.
   */
  let windowImpaired = false

  /**
   * ⚠ **One worker stopping must stop all of them.**
   *
   * The first version recorded only a *refusal*; a **rejection** — a dead
   * connection, a timeout, a cancel — simply propagated out of the one worker
   * that met it. `observed` on a 40 MB file with block 1 dying: `transfer`
   * rejected after 5 requests and **the other nine blocks went up anyway**,
   * bytes that could never be committed because the commit is only reachable
   * once every worker has finished. On a 240 MB routine that is most of a
   * teacher's mobile data spent *after* she was told it failed — and the bar
   * kept climbing beside the error.
   *
   * This is also why the check has to happen **after** the await as well as
   * before it: three siblings are in flight when the fourth dies, and the
   * before-check has already been passed.
   */
  let died: Error | null = null

  const stopping = () => refused !== null || died !== null

  /**
   * Resolves the moment the transfer is over, however it ended.
   *
   * **A worker parked in a ladder wait needs a way out other than the clock.**
   * Without one, a failure recorded by a sibling is not acted on until the
   * current rung expires — up to a minute of a dead transfer sitting on a
   * frozen bar — and a worker waiting on *visibility* never wakes at all if
   * the tab is not brought back.
   */
  let release!: () => void
  const stopped = new Promise<void>((resolve) => {
    release = resolve
  })

  signal?.addEventListener('abort', () => release(), { once: true })

  const halt = (record: () => void) => {
    record()
    release()
  }

  /**
   * One rung: wait to be in front of somebody, then wait out the pause.
   *
   * ⚠ **Returns false when the transfer is over, and the caller must stop.**
   * Both waits are `await`s, and the guards that let the worker in here were
   * all passed *before* them — so without re-checking afterwards, a block is
   * sent up to a minute **after** the transfer has definitively failed, once
   * per worker, on the cellular connection this feature exists for. With a
   * cancel it is worse than wasteful: the re-sent block completes, the commit
   * follows, and `transfer` reports **201** for an upload she stopped.
   */
  const waited = async (rung: number): Promise<boolean> => {
    // The one gate both delays pass through, so the rate's window knows about
    // every one of them from here (D-912).
    windowImpaired = true

    // Hidden means asleep, and a rung spent asleep is a rung wasted.
    await Promise.race([whenInFront(stopped), stopped])

    if (!stopping() && !signal?.aborted) {
      await Promise.race([pause(ladder[rung], signal), stopped])
    }

    if (!stopping() && !signal?.aborted) return true

    /*
     * ⚠ **Leaving without recording anything lets the commit run.** A cancel
     * during a wait sets nothing by itself — the pause resolves rather than
     * rejecting — so the worker would simply return, every worker would
     * finish, and `Put Block List` would go out on an upload she stopped.
     */
    if (signal?.aborted) halt(() => (died ??= new Error('The upload was cancelled.')))

    return false
  }

  const worker = async (): Promise<void> => {
    for (;;) {
      const mine = next++
      if (mine >= jobs.length || stopping()) return

      const job = jobs[mine]
      const slice = file.slice(job.from, Math.min(job.from + BLOCK_BYTES, file.size))

      let put
      let rung = 0

      /*
       * **Only this block is resent, never the file.** Everything already
       * confirmed stays confirmed — which is the whole difference between a
       * drop costing a few seconds and costing the upload.
       */
      for (;;) {
        try {
          put = await send(
            'PUT',
            withQuery(url, `comp=block&blockid=${encodeURIComponent(job.id)}`),
            slice,
            {},
            // Deliberately no per-request progress; see the note above.
            undefined,
            signal,
          )
        } catch (e) {
          /*
           * **A cancel is never retried.** She asked for it to stop, and the
           * ladder would spend two minutes refusing to.
           */
          if (signal?.aborted) {
            halt(() => (died ??= e as Error))
            return
          }

          // A sibling has already failed; this one's sentence is not the news.
          if (stopping()) return

          /*
           * **Is this the connection, or the file?** The transport cannot tell
           * them apart — both arrive as a dead request — and they have
           * opposite remedies. Asking for one byte settles it, and only ever
           * after something has already gone wrong.
           */
          if (!(await stillReadable(slice))) {
            halt(() => (died ??= new Error(
              'That file could not be read any more. Pick it again and the upload will restart.',
            )))
            return
          }

          if (rung >= ladder.length) {
            halt(() => (died ??= e as Error))
            return
          }

          if (!(await waited(rung++))) return

          continue
        }

        // Somebody else stopped while this block was in flight.
        if (stopping()) return

        if (worthAnotherGo(put.status) && rung < ladder.length && !signal?.aborted) {
          if (!(await waited(rung++))) return

          continue
        }

        break
      }

      if (put.status < 200 || put.status >= 300) {
        /*
         * ⚠ **A 403 here does NOT mean "already written".** `observed` against
         * the real account: a block accepted *after* the blob was committed
         * answers 201. So a refused block is a spent or expired permission,
         * and the finishing step cannot rescue it.
         */
        halt(() => (refused = { status: put.status, via: 'storage', text: put.text, settleWithTheFinishingStep: false }))
        return
      }

      confirmed += slice.size
      onProgress?.(confirmed / file.size)

      /*
       * <b>How fast her connection really is</b> (D-912). **What the window is
       * and why it is that, once, at `measureAfter` and `windowImpaired`** —
       * this comment used to carry its own copy of the rule and went on saying
       * *"on the first block"* after that stopped being true, which is the
       * stale-second-copy failure this project keeps paying for.
       *
       * **Confirmed bytes over elapsed time, not bytes handed to the socket**
       * — `xhr.upload.onprogress` reports what the network stack has accepted,
       * which can read 100% while the bytes are still in flight, and a rate
       * computed from that would be fiction in the optimistic direction.
       */
      /*
       * **A block that lands after a sibling was refused is not a measurement.**
       * The transfer is already over; reporting here would hand the screen a
       * rate for an upload about to fail. It is currently harmless only because
       * `offerFor` wants a tile that is still uploading — which is a transport
       * invariant being carried by a screen, so it is checked here instead.
       */
      if (onRate && !rateReported && !windowImpaired && !stopping() && confirmed >= measureAfter) {
        rateReported = true

        const seconds = (performance.now() - begun) / 1000
        if (seconds > 0) onRate({ bytesPerSecond: confirmed / seconds, confirmedBytes: confirmed })
      }
    }
  }

  await Promise.all(Array.from({ length: BLOCKS_IN_FLIGHT }, worker))

  /*
   * **A refusal outranks a death.** Both can be recorded — a sibling dies
   * while this one is refused — and only the refusal carries a status the door
   * can word properly plus a `settleWithTheFinishingStep` worth reading. The
   * transport's sentence is the fallback, not the headline: otherwise a dead
   * sibling masks a 403 and the door never gets to say *"remove it and add it
   * again"*.
   */
  if (refused) return refused
  if (died) throw died

  /*
   * ⚠ **The commit must never run on a partial file**, and this is the one
   * check that does not depend on every exit path having remembered to record
   * why it left. One did not: a cancel during a ladder wait set nothing at
   * all, so every worker finished quietly and `Put Block List` went out on an
   * upload she had stopped — and `transfer` answered 201.
   *
   * A commit naming blocks that were never sent is not a failed upload, it is
   * a **truncated video** nobody looks at again.
   *
   * ⚠ **Nothing tests this, because nothing can currently reach it** — the
   * cancel path above now records properly, and every other exit records a
   * reason. It is kept deliberately: it is one comparison standing between a
   * future exit that forgets and a silently truncated file, and that path was
   * reachable as recently as the commit this invariant was added in.
   */
  if (confirmed !== file.size) {
    throw new Error('The upload stopped before it finished.')
  }

  const list =
    '<?xml version="1.0" encoding="utf-8"?><BlockList>' +
    jobs.map((j) => `<Latest>${j.id}</Latest>`).join('') +
    '</BlockList>'

  const commit = await send(
    'PUT',
    withQuery(url, 'comp=blocklist'),
    list,
    // `content-type` is on the CORS allow-list; nothing else here is.
    { 'Content-Type': 'application/xml' },
    undefined,
    signal,
  )

  return {
    status: commit.status,
    via: 'storage',
    text: commit.text,
    // The one refusal that is ambiguous, and the one the one-write rule rests on.
    settleWithTheFinishingStep: commit.status === 403,
  }
}

/**
 * Everything optional about one transfer.
 *
 * **An object rather than four more positional parameters.** `ladder` had
 * already drifted to sixth place, where two optional arguments before it meant
 * every call site read `transfer(file, to, {}, undefined, undefined, ladder)` —
 * a shape that invites passing the wrong thing in the wrong slot.
 */
/**
 * What one real transfer says about her connection (D-912).
 *
 * ⚠ **Both fields, because the rate alone cannot be acted on.** The
 * measurement is taken some way into the upload, so a caller forecasting from
 * the whole file would quote a wait for bytes that have already gone — which on
 * a small-but-slow upload is how an offer gets made that would cost more time
 * than it saves.
 */
export type Measured = {
  bytesPerSecond: number
  /** What had reached storage when the clock was read. */
  confirmedBytes: number
}

export type TransferOptions = {
  onProgress?: (fraction: number) => void
  signal?: AbortSignal
  /**
   * How fast the bytes are actually moving, measured over the first clean
   * round of blocks (D-912 — `measureAfter` and `windowImpaired` in
   * `putInBlocks` are where that is decided and why).
   *
   * **The only honest source of her upload speed.** `navigator.connection`
   * does not exist on iOS Safari at all, and where it does it estimates
   * *downlink* and buckets everything above 4G together — so it can tell
   * neither 5G from a weak bar nor anything about sending. Real bytes to the
   * real destination answer all of it, on every phone.
   *
   * **At most once per transfer, and three things mean never**: a file small
   * enough to go in one request (there is nothing to decide about an upload
   * already over), a file under `BLOCKS_IN_FLIGHT * BLOCK_BYTES` (the window
   * can only close when the whole thing has landed), and a transfer that
   * waited on the ladder before the window closed.
   */
  onRate?: (measured: Measured) => void
  /**
   * Injected only so a test does not wait on a real clock - the project rule
   * is that a retry pause is made instant rather than slept through.
   */
  ladder?: readonly number[]
}

export async function transfer(
  file: File,
  to: Destination,
  wording: TransferWording,
  options: TransferOptions = {},
): Promise<Transferred> {
  const { onProgress, signal, onRate, ladder = LADDER } = options

  if (to.uploadUrl) {
    const megabytes = Math.round(file.size / (1024 * 1024))
    const step = wording.sending?.(megabytes)

    const written = await (
      /*
       * **A file that fits in one block goes as one request**, exactly as it
       * always did. Blocks buy resilience on a long transfer and cost a round
       * trip, so spending that on something already over in a moment would be
       * all cost.
       */
      file.size > BLOCK_BYTES
        ? putInBlocks(to.uploadUrl, file, onProgress, signal, ladder, onRate)
        : send(
            'PUT',
            to.uploadUrl,
            file,
            storageHeaders(to.contentType ?? file.type),
            onProgress,
            signal,
          ).then((put) => ({
            status: put.status,
            via: 'storage' as const,
            text: put.text,
            settleWithTheFinishingStep: put.status === 403,
          }))
    ).catch((e: Error) => {
      // The step's name goes on here rather than in `during`, which only
      // prefixes a `TypeError` — a failure out of `send` is an ordinary Error.
      throw step ? new Error(`${step}: ${e.message}`) : e
    })

    return written
  }

  /*
   * Through the app. One request, and it must stay one (D-669): this is the
   * path a developer's machine and the entire integration suite take, so a
   * chunked write must never reach it.
   */
  const form = new FormData()
  form.append('file', file, file.name)

  const sent = await send('POST', to.throughApp, form, {}, onProgress, signal)

  return { status: sent.status, via: 'app', text: sent.text, settleWithTheFinishingStep: false }
}

/** Whether storage or the app accepted the bytes. */
export function landed(result: Transferred): boolean {
  return result.status >= 200 && result.status < 300
}
